/**
 * Utility functions for environment detection and common operations
 */

/**
 * Checks if the current environment is local development
 * @returns true if running in local development environment
 */
export function isLocal(): boolean {
  // Check if we're in a browser environment
  if (typeof window !== "undefined") {
    return (
      window.location.hostname === "localhost" ||
      window.location.hostname === "[::1]" ||
      window.location.hostname.includes("localhost")
    );
  }

  // Check if we're in a server environment (Next.js)
  if (typeof process !== "undefined") {
    return (
      process.env.NODE_ENV === "development" ||
      process.env.VERCEL_ENV === "development" ||
      process.env.NEXT_PUBLIC_IS_LOCAL === "true"
    );
  }

  // Default to false if we can't determine
  return false;
}

/**
 * Gets the appropriate API base URL based on the environment
 * @returns The API base URL for the current environment
 */
export function getApiBaseUrl(): string {
  if (isLocal() && !usesRemoteBackend()) {
    return "http://localhost:8000";
  }
  if (process.env.NEXT_PUBLIC_BACKEND_API_URL) {
    return process.env.NEXT_PUBLIC_BACKEND_API_URL;
  }
  throw new Error("NEXT_PUBLIC_BACKEND_API_URL is not set");
}

/**
 * Opt-in escape hatch for pointing local development at the deployed backend,
 * for work that needs real data (published landing pages, live reporting) and
 * where running the whole backend stack locally is not worth it.
 *
 * Off by default on purpose: with this on, local writes go to the production
 * database and backend endpoints that only exist locally will 404. Set
 * NEXT_PUBLIC_USE_REMOTE_BACKEND=true in .env.local deliberately, and unset it
 * before doing anything that writes.
 *
 * @returns true when local development should use NEXT_PUBLIC_BACKEND_API_URL
 */
export function usesRemoteBackend(): boolean {
  return (
    typeof process !== "undefined" &&
    process.env.NEXT_PUBLIC_USE_REMOTE_BACKEND === "true" &&
    Boolean(process.env.NEXT_PUBLIC_BACKEND_API_URL)
  );
}

/**
 * Gets the appropriate scheme (http/https) based on the environment
 * @returns 'http' for local development, 'https' for production
 */
export function getScheme(): string {
  return isLocal() ? "http" : "https";
}

/**
 * Gets the current environment name
 * @returns 'development', 'production', or 'test'
 */
export function getEnvironment(): string {
  if (typeof process !== "undefined") {
    return process.env.NODE_ENV || "development";
  }
  return "development";
}

/**
 * Checks if the current environment is production
 * @returns true if running in production environment
 */
export function isProduction(): boolean {
  return getEnvironment() === "production";
}

/**
 * Checks if the current environment is development
 * @returns true if running in development environment
 */
export function isDevelopment(): boolean {
  return getEnvironment() === "development";
}

/**
 * Gets the hostname for the current environment
 * @returns The appropriate hostname based on environment
 */
export function getHostname(): string {
  if (typeof window !== "undefined") {
    return window.location.hostname;
  }

  // For server-side, we might need to get this from headers
  return "localhost";
}
